import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import MqttClient from "mqtt/lib/client";
import websocket from "websocket-stream";
import { notification, Icon } from "antd";
import * as R from "ramda";
import i18n from "i18next";
import { Match } from "@worcket/common/components/feedback";
import shortid from "shortid";
import ms from "ms";

import api from "../utils/api";
import { createApiThunk } from "../utils/thunk";
import {
  CLIENT_CONNECT,
  messageRead,
  NEW_MESSAGE
} from "../store/actions/chat";
import {
  NEW_CHATBOT_TEST_MESSAGE,
  FETCH_CHATBOT_CREATED
} from "../store/actions/opportunity";
import { FETCH_NEW_MESSAGES } from "../store/actions/chat";
import history from "../history";

let client = null;

const getNewMessages = createApiThunk({
  action: FETCH_NEW_MESSAGES,
  request: async companyId => {
    const { messages } = await api.messages.unreadMessages(companyId);
    return messages;
  }
});

export const useClientConnect = companyId => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const user = useSelector(state => state.user);

  // Prevent Web sockets connection if running Cypress tests
  const connect = useCallback(async () => {
    try {
      client && client.end();

      dispatch(getNewMessages(companyId));

      // Prevent Web sockets connection if running Cypress tests
      if (window.Cypress) return;

      dispatch(CLIENT_CONNECT.start({ company: companyId }));

      const { url } = await api.chat.getConnectionData(companyId);

      client = new MqttClient(() => websocket(url, ["mqtt"]), {
        clientId: `client_${companyId}_${shortid.generate()}`,
        reconnectPeriod: 5000
      });

      client.on("connect", () => {
        client.subscribe("presence-subscriptions-events");

        dispatch(CLIENT_CONNECT.resolve({ url }));
      });

      client.on("close", () => {
        console.warn("Connection closed");
      });

      client.on("message", async (topic, message) => {
        try {
          const decodedMessage = R.pipe(
            R.toString,
            R.defaultTo("{}"),
            JSON.parse
          )(message);

          if (decodedMessage.topic.startsWith("test/")) {
            if (R.propEq("text", "[chatbot_created]", decodedMessage)) {
              dispatch(FETCH_CHATBOT_CREATED.resolve(true));
              return;
            }

            dispatch(NEW_CHATBOT_TEST_MESSAGE.run(decodedMessage));
            return;
          }

          switch (decodedMessage.type) {
            case "newLike": {
              /*
              // newLike
              {
                idCandidate: string,
                idCompany: string,
                idOpportunity: string,
                like: boolean,
                source: "candidate",
                topic: string,
                type: "newLike"
              }
            */

              const {
                idOpportunity,
                idCandidate,
                candidateName,
                opportunityTitle
              } = decodedMessage;

              if (user.notifications) {
                return notification.open({
                  message: (
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        history.push(
                          `/oportunidades/${idOpportunity}/candidatos/sugeridos/${idCandidate}`
                        )
                      }
                    >
                      {i18n.t(
                        "toasts:candidates.candidateLikedOpportunityTitle"
                      )}
                    </div>
                  ),
                  description: (
                    <div>
                      {i18n.t(
                        "toasts:candidates.candidateLikedOpportunityDescription",
                        {
                          candidateName,
                          opportunityTitle
                        }
                      )}
                    </div>
                  ),
                  duration: 10,
                  icon: <Icon type="like" style={{ color: "#52c41a" }} />
                });
              }
              break;
            }
            case "newMatch": {
              /*
              // newMatch
              {
                idCandidate: string,
                idCompany: string,
                idOpportunity: string,
                like: boolean,
                source: "candidate",
                topic: string,
                type: "newMatch"
              }
            */

              const {
                idOpportunity,
                idCandidate,
                candidateAvatar,
                candidateName,
                opportunityTitle
              } = decodedMessage;

              const matchModalKey = shortid.characters(
                "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ$@"
              );

              if (user.notifications) {
                return notification.open({
                  key: matchModalKey,
                  description: (
                    <Match
                      t={i18n.t.bind(i18n)}
                      avatar={candidateAvatar}
                      title={`${candidateName} - ${opportunityTitle}`}
                      onOpen={() => {
                        history.push(
                          `/oportunidades/${idOpportunity}/proceso/${idCandidate}/chat`
                        );
                        notification.close(matchModalKey);
                      }}
                      openLabel={i18n.t("common:match.startChat")}
                    />
                  )
                });
              }
              break;
            }
            default: {
              const {
                source,
                idOpportunity,
                idCandidate,
                idCompany
              } = decodedMessage;
              const { candidates } = getState();

              const isMessageFromActiveChat =
                R.path(["selected", "id"], candidates) === idCandidate &&
                history.location.pathname.endsWith("/chat");

              switch (source) {
                case "candidate": {
                  if (isMessageFromActiveChat) {
                    dispatch(
                      messageRead({
                        idOpportunity,
                        idCandidate,
                        idCompany
                      })
                    );
                  }
                  break;
                }
                case "company":
                  break;
                default: {
                  console.log("Unknown source for ", decodedMessage);
                  break;
                }
              }

              dispatch(
                NEW_MESSAGE.run(
                  R.when(
                    R.always(isMessageFromActiveChat),
                    R.assoc("viewed", true),
                    decodedMessage
                  )
                )
              );
            }
          }
        } catch (error) {
          console.log("Unable to decode message");
          console.log({ message: message.toString() });
          console.log(error);
        }
      });

      client.subscribe(`/${companyId}/#`);
    } catch (error) {
      dispatch(CLIENT_CONNECT.reject(error));
    }
  }, [companyId, dispatch, getState, user.notifications]);

  const reconnect = useCallback(() => {
    client && client.end();
    connect();
  }, [connect]);

  const reconnectInterval = React.useRef(null);

  useEffect(() => {
    if (companyId) {
      connect();
    }
  }, [companyId, connect]);

  useEffect(() => {
    reconnectInterval.current = setInterval(() => {
      reconnect();
    }, ms("10m"));
    return () => {
      clearInterval(reconnectInterval.current);
    };
  }, [reconnect]);
};

export const subscribeToTopic = topic => client && client.subscribe(topic);

export const unsubscribeFromTopic = topic =>
  client && client.unsubscribe(topic);
